import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {getEdpAirflowInfo} from "../../../../utils/airflow";
import {convertToQueryParams} from "../../../../utils/urlBuilder";
import RunIdLink from "../../../IngestionLogs/Columns/RunIdLink";
import {getRetailerName} from "../../../../utils/localCache/retailerMapCache";
import RetailerIdAndName from "../../RetailerIdAndName";
import JsonPayloadModal from "../../JsonPayloadModal";
import {makeApiCall} from "../../../../Api/makeApiCall";
import {sortAscendingWithNullsFirst} from "../../../../utils/sorting";
import {useServiceBase} from "../../ReactTable/useServiceBase";
import {LoadingWrapper} from "../../LoadingWrapper";
import ReactTable from "../../ReactTable";
import {DEFAULT_DAG_LOG_POLL_INTERVAL_MS, hasActiveDagRuns} from "./dagLogUtils";

const AirflowDagLogs = ({
                            retailerId = undefined,
                            processName = undefined,
                            customColumns = [],
                            includeDagName = false,
                            includeRetailer = false,
                            includeVersion = false,
                            refreshTrigger = undefined,
                            setAutoRefresh = false,
                            pollIntervalMs = DEFAULT_DAG_LOG_POLL_INTERVAL_MS
                        }) => {

    const columns = [
        {
            header: 'Run Id',
            accessorKey: 'runid',
            className: 'non-numeric-field',
            cell: ({row, getValue}) => <RunIdLink airflowUrl={airflowInfo?.uri} airflowVersion={airflowInfo?.airflowVersion} {...row} value={getValue()} />,
        },
        ...(includeVersion ? [{header: 'Version', accessorKey: 'conf.version', className: 'non-numeric-field'}] : []),
        ...(includeRetailer ? [{
            header: 'Retailer',
            accessorKey: 'retailerid',
            className: 'numeric-field',
            filterFn: (row, columnId, filterValue) => {
                return !filterValue || Boolean(`${row.original.retailerid} ${getRetailerName(row.original.retailerid)?.toLowerCase()}`.includes(filterValue.toLowerCase()));
            },
            cell: ({row}) => {
                const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/getGcsFileByPath?path=${encodeURIComponent(row.original?.retailer_id_url)}`;
                let retailerId
                if (row.original.retailerid === null || row.original.retailerid === "")
                    retailerId = "All"
                else if (row.original.retailerid)
                    retailerId = <RetailerIdAndName retailerId={row.original.retailerid}/>
                return (<div>
                    {
                        row.original?.retailer_id_url && row.original?.retailer_id_url.startsWith("gs://") ?
                            <JsonPayloadModal buttonText={"Retailers"}
                                              onClick={() => makeApiCall(endpointUrl)
                                                  .then((result) => result)}/>
                            : row.original?.retailer_id_url || retailerId}
                </div>)
            }
        }] : []),
        ...(includeDagName ? [{header: 'Dag Name', accessorKey: 'process', className: 'non-numeric-field'}] : []),
        ...customColumns,
        {
            header: 'Triggered By',
            accessorKey: 'triggeredby',
            className: 'non-numeric-field',
            sortingFn: (a, b, columnId) => {
                const valA = a.getValue(columnId);
                const valB = b.getValue(columnId);
                if (valA && valB) {
                    return valA.localeCompare(valB);
                }
                return sortAscendingWithNullsFirst(valA, valB);
            }
        },
        {
            header: 'Run Configuration',
            accessorKey: 'conf',
            enableColumnFilter: false,
            cell: ({getValue}) => <JsonPayloadModal value={getValue()}/>,
        }
    ]

    const [airflowInfo, updateAirflowInfo] = useState(undefined)

    const queryStringParams = convertToQueryParams({retailerId, processName})
    const queryString = queryStringParams ? `?${queryStringParams}` : ''
    const isWaitingForProcessNames = Array.isArray(processName) && processName.length === 0;
    const endpointUrl = isWaitingForProcessNames ? undefined : `${process.env.REACT_APP_EDP_API_BASE_URL}/airflow/daglogs${queryString}`;

    const {loading, data, refreshData} = useServiceBase({endpointUrl});
    const shouldPoll = !loading && hasActiveDagRuns(data?.metadata);

    useEffect(() => {
        getEdpAirflowInfo().then(edpAirflowInfo => updateAirflowInfo(edpAirflowInfo))
    }, [])

    useEffect(() => {
        if (refreshTrigger != null) {
            refreshData();
        }
    }, [refreshTrigger, refreshData]);

    useEffect(() => {
        if (!shouldPoll || !setAutoRefresh) {
            return undefined;
        }
        const intervalId = setInterval(refreshData, pollIntervalMs);
        return () => clearInterval(intervalId);
    }, [shouldPoll, pollIntervalMs, refreshData, setAutoRefresh]);

    return (
        <LoadingWrapper loading={loading}>
            <ReactTable
                data={data.metadata}
                columns={columns}
                filterable
                getTrProps={(rowInfo) => {
                    const style = {};
                    if (rowInfo && rowInfo.original && rowInfo.original.status) {
                        if (rowInfo.original.status.toLowerCase() === 'failed') {
                            style['borderLeft'] = '4px solid #FA7978';
                        } else if (rowInfo.original.is_complete === true) {
                            style['borderLeft'] = '4px solid #95E969';
                        }
                    }
                    return {
                        style
                    };
                }}
            />
        </LoadingWrapper>
    )

}

AirflowDagLogs.propTypes = {
    retailerId: PropTypes.number,
    processName: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    customColumns: PropTypes.arrayOf(PropTypes.object),
    includeDagName: PropTypes.bool,
    includeRetailer: PropTypes.bool,
    includeVersion: PropTypes.bool,
    setAutoRefresh: PropTypes.bool,
    refreshTrigger: PropTypes.number,
    pollIntervalMs: PropTypes.number
}

export default AirflowDagLogs;
